import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import Icon from "components/AppIcon";
import { useAddIndicator, useAddSubIndicator, useGetAllIndicators, useGetAllSubIndicators, useUpdateIndicator, useUpdateSubIndicator } from "service/setup.service";
import useAuth from "hooks/useAuth";


const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const DEFAULT_FORM = {
  code: "",
  name: "",
  status: "Inactive",
  description: "",
};

const DEFAULT_SUB_INDICATOR_FORM = {
  subCode: "",
  subName: "",
  status: "Inactive",
  description: "",
  maximumScore: 0
};


function statusClass(status) {
  if (status === "Active") {
    return "bg-success/10 text-success";
  }

  if (status === "Inactive") {
    return "bg-muted text-muted-foreground";
  }

  return "bg-warning/10 text-warning";
}

function getIndicatorCoverage(indicator) {
  const groupCount = indicator?.subGroups?.length || 0;
  const subCount = (indicator?.subGroups || []).reduce((sum, item) => sum + item.subIndicators.length, 0);

  return { groupCount, subCount };
}

function RowActionMenu({ isOpen, onToggle, items }) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  const updateMenuPosition = () => {
    if (!triggerRef.current) {
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight || items.length * 36 + 10;
    const menuWidth = menuRef.current?.offsetWidth || 144;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const openUpwards = spaceBelow < menuHeight + 8;

    const top = openUpwards
      ? Math.max(8, triggerRect.top - menuHeight - 6)
      : Math.min(window.innerHeight - menuHeight - 8, triggerRect.bottom + 6);
    const left = Math.min(
      Math.max(8, triggerRect.right - menuWidth),
      window.innerWidth - menuWidth - 8,
    );

    setMenuPosition({ top, left });
  };

  useLayoutEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const frame = requestAnimationFrame(updateMenuPosition);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen, items.length]);

  return (
    <div className="inline-flex" onClick={(event) => event.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        aria-label="Open actions menu"
      >
        <Icon name="MoreHorizontal" className="h-4 w-4" />
      </button>

      {isOpen ? (
        createPortal(
          <div
            ref={menuRef}
            style={{ top: menuPosition.top, left: menuPosition.left }}
            className="fixed z-[1500] min-w-[9rem] rounded-md border border-border bg-card p-1 shadow-lg"
            onClick={(event) => event.stopPropagation()}
          >
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  item.onClick();
                }}
                className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-medium transition-colors ${item.tone === "destructive"
                  ? "text-destructive hover:bg-destructive/10"
                  : "text-foreground hover:bg-muted"
                  }`}
              >
                <Icon name={item.icon} className="h-3.5 w-3.5" />
                {item.label}
              </button>
            ))}
          </div>,
          document.body,
        )
      ) : null}
    </div>
  );
}

function IndicatorModal({ isOpen, mode, formValues, formError, onClose, onSubmit, onReset, onFieldChange }) {
  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[1200] bg-black/50" onClick={onClose} />
      <div className="fixed inset-0 z-[1300] overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-card shadow-xl border border-border">
            <div className="flex items-center justify-between gap-4 border-b border-border px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon name={mode === "edit" ? "Edit" : "PlusSquare"} className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    {mode === "edit" ? "Edit RCC Indicator" : "Add RCC Indicator"}
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {mode === "edit"
                      ? "Update indicator details used for RCC setup and reporting."
                      : "Create an indicator, then select it from the table to manage sub-indicator groups."}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Icon name="X" className="h-4 w-4" />
              </button>
            </div>

            <form className="space-y-4 px-6 py-5" onSubmit={onSubmit}>
              <Input
                label="Indicator Code"
                placeholder="RCC-004"
                value={formValues.code}
                onChange={(event) => onFieldChange("code", event.target.value)}
              />

              <Input
                label="Indicator Name"
                placeholder="Enter indicator name"
                value={formValues.name}
                onChange={(event) => onFieldChange("name", event.target.value)}
              />

              <Select
                label="Status"
                options={STATUS_OPTIONS}
                value={STATUS_OPTIONS.find((option) => option.value === formValues.status) || null}
                onChange={(option) => onFieldChange("status", option?.value || STATUS_OPTIONS[0].value)}
              />

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground" htmlFor="rcc-indicator-description">
                  Description
                </label>
                <textarea
                  id="rcc-indicator-description"
                  rows={4}
                  value={formValues.description}
                  onChange={(event) => onFieldChange("description", event.target.value)}
                  placeholder="Add notes about scoring, source data, or expected use."
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground transition-[border-color,box-shadow] focus-visible:outline-none focus-visible:border-[#16325a] focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:shadow-[0_0_0_3px_rgba(22,50,90,0.12)]"
                />
              </div>

              {formError && (
                <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                  {formError}
                </div>
              )}

              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-border pt-4">
                <Button type="button" variant="outline" onClick={onReset}>Reset Form</Button>
                <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                <Button type="submit">
                  <Icon name={mode === "edit" ? "Save" : "Plus"} className="h-4 w-4" />
                  {mode === "edit" ? "Update Indicator" : "Save Indicator"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

function SubIndicatorModal({
  isOpen,
  mode,
  groupName,
  formValues,
  formError,
  onClose,
  onSubmit,
  onReset,
  onFieldChange,
}) {
  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[1200] bg-black/50" onClick={onClose} />
      <div className="fixed inset-0 z-[1300] overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="w-full max-w-xl rounded-2xl border border-border bg-card shadow-xl">
            <div className="flex items-center justify-between gap-4 border-b border-border px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon name={mode === "edit" ? "Edit" : "PlusSquare"} className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    {mode === "edit" ? "Edit Sub-indicator" : "Add Sub-indicator"}
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {mode === "edit" ? "Update the selected sub-indicator under " : "Add a new sub-indicator under "}
                    {groupName || "the selected group"}.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Icon name="X" className="h-4 w-4" />
              </button>
            </div>

            <form className="space-y-4 px-6 py-5" onSubmit={onSubmit}>
              <Input
                label="Sub Indicator Name"
                placeholder="Enter sub-indicator name"
                value={formValues.subName}
                onChange={(event) => onFieldChange("subName", event.target.value)}
              />

              <Input
                label="Sub Indicator Code"
                placeholder="Enter code"
                value={formValues.subCode}
                onChange={(event) => onFieldChange("subCode", event.target.value)}
              />

              <Input
                label="Indicator Maximum Score"
                placeholder="Enter Score"
                value={formValues.maximumScore}
                onChange={(event) => onFieldChange("maximumScore", event.target.value)}
              />

              <Select
                label="Status"
                options={STATUS_OPTIONS}
                value={STATUS_OPTIONS.find((option) => option.value === formValues.status) || null}
                onChange={(option) => onFieldChange("status", option?.value || STATUS_OPTIONS[0].value)}
              />

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground" htmlFor="description">
                  Description
                </label>
                <textarea
                  id="description"
                  rows={4}
                  value={formValues.description}
                  onChange={(event) => onFieldChange("description", event.target.value)}
                  placeholder="Add notes about scoring, source data, or expected use."
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground transition-[border-color,box-shadow] focus-visible:outline-none focus-visible:border-[#16325a] focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:shadow-[0_0_0_3px_rgba(22,50,90,0.12)]"
                />
              </div>

              {formError && (
                <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                  {formError}
                </div>
              )}

              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-border pt-4">
                <Button type="button" variant="outline" onClick={onReset}>Reset Form</Button>
                <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                <Button type="submit">
                  <Icon name={mode === "edit" ? "Save" : "Plus"} className="h-4 w-4" />
                  {mode === "edit" ? "Update Sub-indicator" : "Save Sub-indicator"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

export default function RccIndicatorSetupWorkspace({ activeModule }) {
  const [formValues, setFormValues] = useState(DEFAULT_FORM);
  const [formError, setFormError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIndicatorId, setEditingIndicatorId] = useState(null);
  const [editingIndicator, setEditingIndicator] = useState(null);
  const [openIndicatorActionId, setOpenIndicatorActionId] = useState(null);
  const [openSubIndicatorActionKey, setOpenSubIndicatorActionKey] = useState(null);
  const [indicatorSearch, setIndicatorSearch] = useState("");
  const [selectedIndicatorId, setSelectedIndicatorId] = useState(null);
  const [subIndicatorFormValues, setSubIndicatorFormValues] = useState(DEFAULT_SUB_INDICATOR_FORM);
  const [subIndicatorFormError, setSubIndicatorFormError] = useState("");
  const [subIndicatorTarget, setSubIndicatorTarget] = useState(null);

  const { user } = useAuth();

  //Indicators group Integration
  const { data: indicators, error, isFetched, isLoading, isSuccess } = useGetAllIndicators();
  const { mutate: addIndicatorGroup } = useAddIndicator();
  const { mutate: updateIndicatorGroup } = useUpdateIndicator();

  //Sub Indicators Integration
  const { data: subIndicators, error: subError,
    isFetched: isSubFetched, isLoading: isSubLoading, isSuccess: isSubSuccess } = useGetAllSubIndicators();
  const { mutate: addSubIndicator } = useAddSubIndicator();
  const { mutate: updateSubIndicator } = useUpdateSubIndicator();

  const indicatorList = indicators?.data || [];

  const activeCount = useMemo(
    () => indicatorList.filter((indicator) => indicator.status === "Active").length,
    [indicators],
  );

  const inactiveCount = indicatorList.length - activeCount;
  const selectedIndicator = useMemo(
    () => indicatorList.find((indicator) => indicator.indicatorId === selectedIndicatorId) || indicatorList[0] || null,
    [indicators, selectedIndicatorId],
  );
  const { groupCount: selectedGroupCount, subCount: selectedSubCount } = useMemo(
    () => getIndicatorCoverage(selectedIndicator),
    [selectedIndicator],
  );

  const filteredIndicators = useMemo(() => {
    const query = indicatorSearch.trim().toLowerCase();

    if (!query) return indicatorList;

    return indicatorList.filter((indicator) =>
      [indicator.code, indicator.name, indicator.status, indicator.description]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [indicatorSearch, indicatorList]);

  const handleFieldChange = (field, value) => {
    setFormValues((current) => ({ ...current, [field]: value }));
  };
  const handleSubIndicatorFieldChange = (field, value) => {
    setSubIndicatorFormValues((current) => ({ ...current, [field]: value }));
  };

  useEffect(() => {
    const closeActionMenus = () => {
      setOpenIndicatorActionId(null);
      setOpenSubIndicatorActionKey(null);
    };

    document.addEventListener("click", closeActionMenus);
    return () => {
      document.removeEventListener("click", closeActionMenus);
    };
  }, []);

  const handleReset = () => {
    setFormValues(DEFAULT_FORM);
    setFormError("");
  };
  const handleSubIndicatorReset = () => {
    setSubIndicatorFormValues(DEFAULT_SUB_INDICATOR_FORM);
    setSubIndicatorFormError("");
  };

  const closeModal = () => {
    handleReset();
    setEditingIndicatorId(null);
    setEditingIndicator(null);
    setIsModalOpen(false);
  };
  const closeSubIndicatorModal = () => {
    handleSubIndicatorReset();
    setSubIndicatorTarget(null);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!formValues.code.trim() || !formValues.name.trim()) {
      setFormError("Code and indicator name are required.");
      return;
    }

    const indicatorCode = formValues.code.trim().toUpperCase();
    if (indicatorList.some((indicator) => indicator.code === indicatorCode && indicator.indicatorId !== editingIndicatorId)) {
      setFormError("Indicator code already exists in the RCC library.");
      return;
    }

    const timestamp = new Date().toISOString();

    const payload = {
      indicatorId: editingIndicator ? editingIndicator?.indicatorId : 0,
      code: indicatorCode,
      name: formValues.name.trim(),
      description: formValues.description.trim(),
      status: formValues.status,
      createdAt: editingIndicator ? editingIndicator?.createdAt : timestamp,
      updatedAt: timestamp,
      createdBy: editingIndicator ? editingIndicator?.createdBy : user?.user?.username,
      updatedBy: user?.user?.username
    };

    if (editingIndicator) {
      updateIndicatorGroup(
        {
          id: editingIndicator.indicatorId,
          payload: payload
        })
    } else {

      addIndicatorGroup(payload);

    }

    closeModal();
  };
  const openAddIndicatorModal = () => {
    handleReset();
    setEditingIndicatorId(null);
    setEditingIndicator(null);
    setIsModalOpen(true);
  };
  const openEditIndicatorModal = (indicator) => {
    if (!indicator) {
      return;
    }

    setFormValues({
      code: indicator.code || "",
      name: indicator.name || "",
      status: indicator.status || STATUS_OPTIONS[0].value,
      description: indicator.description || "",
    });
    setFormError("");
    setEditingIndicatorId(indicator.indicatorId);
    setEditingIndicator(indicator);
    setOpenIndicatorActionId(null);
    setIsModalOpen(true);
  };
  const openSubIndicatorModal = (groupId) => {
    if (!selectedIndicator) {
      return;
    }

    handleSubIndicatorReset();
    setOpenSubIndicatorActionKey(null);
    setSubIndicatorTarget({ indicatorId: selectedIndicator.indicatorId, groupId });
  };
  const openEditSubIndicatorModal = (groupId, subIndicator) => {
    if (!selectedIndicator || !subIndicator) {
      return;
    }

    setSubIndicatorFormValues({
      subName: subIndicator.subName || "",
      subCode: subIndicator.subCode || "",
      status: subIndicator.status || "",
      maximumScore: subIndicator.maximumScore,
      indicatorId: subIndicator?.indicator?.indicatorId,
      description: subIndicator.description || ""
    });
    setSubIndicatorFormError("");
    setSubIndicatorTarget({
      indicatorId: selectedIndicator.indicatorId,
      groupId,
      subIndicatorId: subIndicator.subIndicatorId,
    });
    setOpenSubIndicatorActionKey(null);
  };

  const handleSubIndicatorSubmit = (event) => {
    event.preventDefault();

    if (!subIndicatorFormValues.subName.trim() || subIndicatorFormValues?.maximumScore == 0) {
      setSubIndicatorFormError("Sub-indicator name and maximum score are required.");
      return;
    }


    if (!subIndicatorTarget) {
      setSubIndicatorFormError("Select an indicator group before adding a sub-indicator.");
      return;
    }

    const timestamp = new Date().toISOString();

    const payload = {
      subIndicatorId: subIndicatorTarget?.subIndicatorId || 0,
      indicatorId: selectedIndicator.indicatorId,
      subCode: subIndicatorFormValues.subCode.trim(),
      subName: subIndicatorFormValues.subName.trim(),
      maximumScore: subIndicatorFormValues.maximumScore,
      description: subIndicatorFormValues.description.trim(),
      status: subIndicatorFormValues.status,
      createdAt: editingIndicator ? editingIndicator?.createdAt : timestamp,
      updatedAt: timestamp,
      createdBy: editingIndicator ? editingIndicator?.createdBy : user?.user?.username,
      updatedBy: user?.user?.username
    };

    if (payload.subIndicatorId > 0) {
      updateSubIndicator(
        {
          id: subIndicatorTarget.subIndicatorId,
          payload: payload
        })
    } else {

      addSubIndicator(payload);

    }

    closeSubIndicatorModal();
  };

  const handleRemoveSubIndicator = (groupId, subIndicatorId) => {
    if (!selectedIndicator) {
      return;
    }

    if (subIndicatorTarget?.subIndicatorId === subIndicatorId) {
      closeSubIndicatorModal();
    }

    setOpenSubIndicatorActionKey(null);
  };

  const handleRemove = (indicatorId) => {

    if (editingIndicatorId === indicatorId) {
      closeModal();
    }

    setOpenIndicatorActionId(null);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <IndicatorModal
        isOpen={isModalOpen}
        mode={editingIndicatorId ? "edit" : "add"}
        formValues={formValues}
        formError={formError}
        onClose={closeModal}
        onSubmit={handleSubmit}
        onReset={handleReset}
        onFieldChange={handleFieldChange}
      />
      <SubIndicatorModal
        isOpen={Boolean(subIndicatorTarget)}
        mode={subIndicatorTarget?.subIndicatorId ? "edit" : "add"}
        groupName={selectedIndicator?.name}
        formValues={subIndicatorFormValues}
        formError={subIndicatorFormError}
        onClose={closeSubIndicatorModal}
        onSubmit={handleSubIndicatorSubmit}
        onReset={handleSubIndicatorReset}
        onFieldChange={handleSubIndicatorFieldChange}
      />

      <section className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
              <Icon name={activeModule.icon} className="h-3.5 w-3.5" />
              RCC Workspace
            </div>
            <h2 className="text-2xl font-semibold text-foreground">{activeModule.label}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{activeModule.summary}</p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div className="rounded-xl border border-border bg-background px-4 py-3">
                <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Library Size</p>
                <p className="mt-1 text-xl font-semibold text-foreground">{indicatorList.length}</p>
              </div>
              <div className="rounded-xl border border-border bg-background px-4 py-3">
                <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Active</p>
                <p className="mt-1 text-xl font-semibold text-foreground">{activeCount}</p>
              </div>
              <div className="rounded-xl border border-border bg-background px-4 py-3">
                <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Inactive</p>
                <p className="mt-1 text-xl font-semibold text-foreground">{inactiveCount}</p>
              </div>
            </div>

            <Button type="button" onClick={openAddIndicatorModal}>
              <Icon name="Plus" className="h-4 w-4" />
              Add Indicator
            </Button>
          </div>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[minmax(0,0.92fr)_minmax(0,1.28fr)]">
        <article className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                <Icon name="TableProperties" className="h-3.5 w-3.5" />
                Indicator Table
              </div>
              <h3 className="mt-3 text-lg font-semibold text-foreground">RCC Indicator Library</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Click any indicator to inspect its sub-indicator groups on the right.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full bg-success/10 px-3 py-1 text-xs font-medium text-success">
                {activeCount} active
              </span>
              <span className="inline-flex items-center rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                {inactiveCount} inactive
              </span>
            </div>
          </div>

          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-sm">
              <label className="sr-only" htmlFor="rcc-indicator-search">Search indicators</label>
              <Icon
                name="Search"
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              />
              <input
                id="rcc-indicator-search"
                type="search"
                value={indicatorSearch}
                onChange={(event) => setIndicatorSearch(event.target.value)}
                placeholder="Search by code, name, or status"
                className="flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground transition-[border-color,box-shadow] focus-visible:outline-none focus-visible:border-[#16325a] focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:shadow-[0_0_0_3px_rgba(22,50,90,0.12)]"
              />
            </div>

            {indicatorSearch.trim() ? (
              <p className="text-xs text-muted-foreground">
                {filteredIndicators.length} result{filteredIndicators.length === 1 ? "" : "s"}
              </p>
            ) : null}
          </div>

          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Indicator</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {filteredIndicators.map((indicator) => {
                  const isSelected = selectedIndicator?.indicatorId === indicator.indicatorId;
                  const { groupCount, subCount } = getIndicatorCoverage(indicator);

                  return (
                    <tr
                      key={indicator.indicatorId}
                      className={`cursor-pointer align-top transition-colors ${isSelected ? "bg-muted" : "hover:bg-muted/30"
                        }`}
                      onClick={() => setSelectedIndicatorId(indicator.indicatorId)}
                    >
                      <td className="px-4 py-3 font-medium text-foreground">{indicator.code}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-foreground">{indicator.name}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {subIndicators?.data?.filter(sub => sub.indicator.indicatorId === indicator.indicatorId).length | 0} sub-indicator(s)
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusClass(indicator.status)}`}>
                          {indicator.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <RowActionMenu
                          isOpen={openIndicatorActionId === indicator.indicatorId}
                          onToggle={() =>
                            setOpenIndicatorActionId((current) => (current === indicator.indicatorId ? null : indicator.indicatorId))
                          }
                          items={[
                            {
                              key: "edit",
                              label: "Edit",
                              icon: "Edit",
                              onClick: () => openEditIndicatorModal(indicator),
                            },
                            {
                              key: "remove",
                              label: "Remove",
                              icon: "Trash2",
                              tone: "destructive",
                              onClick: () => handleRemove(indicator.indicatorId),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })}
                {filteredIndicators.length === 0 ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-sm text-muted-foreground" colSpan={4}>
                      No indicators match the current search.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </article>

        <article className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          {selectedIndicator ? (
            <div className="space-y-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    <Icon name="Layers3" className="h-3.5 w-3.5" />
                    Sub-indicator Groups
                  </div>
                  <h3 className="mt-3 text-lg font-semibold text-foreground">{selectedIndicator.name}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{selectedIndicator.description}</p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-foreground">
                    {selectedIndicator.code}
                  </span>
                  <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${statusClass(selectedIndicator.status)}`}>
                    {selectedIndicator.status}
                  </span>

                </div>
              </div>



              {!selectedIndicator ? (
                <div className="rounded-2xl border border-dashed border-border bg-muted/20 px-5 py-10 text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
                    <Icon name="Layers3" className="h-5 w-5" />
                  </div>
                  <h4 className="mt-4 text-base font-semibold text-foreground">No sub-indicator groups yet</h4>
                  <p className="mx-auto mt-2 max-w-lg text-sm text-muted-foreground">
                    This indicator exists in the library, but its sub-indicators or groups have not been configured yet.
                  </p>
                </div>
              ) : (
                <div className="grid gap-4">

                  <section key={selectedIndicator.indicatorId} className="rounded-2xl border border-border bg-background p-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div>

                      </div>

                      <div className="flex flex-wrap items-center gap-2">

                        <Button type="button" variant="outline" size="sm" onClick={() => openSubIndicatorModal(selectedIndicator?.indicatorId)}>
                          <Icon name="Plus" className="h-4 w-4" />
                          Add Sub-indicator
                        </Button>
                      </div>
                    </div>

                    <div className="mt-4 overflow-x-auto rounded-xl border border-border">
                      <table className="min-w-full divide-y divide-border text-sm">
                        <thead className="bg-card">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Sub-indicator</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Code</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Max Score</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Status</th>

                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border bg-background">
                          {subIndicators?.data?.filter(sub => sub.indicator.indicatorId === selectedIndicator.indicatorId).length === 0 ? (
                            <tr>
                              <td className="px-4 py-6 text-center text-sm text-muted-foreground" colSpan={4}>
                                No sub-indicators added yet.
                              </td>
                            </tr>
                          ) : (
                            subIndicators?.data?.filter(sub => sub.indicator.indicatorId === selectedIndicator.indicatorId).map((subItem) => (
                              <tr key={subItem.id}>
                                <td className="px-4 py-3 font-medium text-foreground">{subItem.subName}</td>
                                <td className="px-4 py-3 text-muted-foreground">{subItem.subCode}</td>
                                <td className="px-4 py-3 text-muted-foreground">{subItem.maximumScore}</td>
                                <td className="px-4 py-3 text-muted-foreground">
                                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusClass(subItem.status)}`}>
                                    {subItem.status}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex justify-end">
                                    <RowActionMenu
                                      isOpen={openSubIndicatorActionKey === `${selectedIndicator.indicatorId}:${subItem.subIndicatorId}`}
                                      onToggle={() =>
                                        setOpenSubIndicatorActionKey((current) =>
                                          current === `${selectedIndicator.indicatorId}:${subItem.subIndicatorId}` ? null : `${selectedIndicator.indicatorId}:${subItem.subIndicatorId}`,
                                        )
                                      }
                                      items={[
                                        {
                                          key: "edit",
                                          label: "Edit",
                                          icon: "Edit",
                                          onClick: () => openEditSubIndicatorModal(selectedIndicator.indicatorId, subItem),
                                        },
                                        {
                                          key: "remove",
                                          label: "Remove",
                                          icon: "Trash2",
                                          tone: "destructive",
                                          onClick: () => handleRemoveSubIndicator(selectedIndicator.indicatorId, subItem.subIndicatorId),
                                        },
                                      ]}
                                    />
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </section>

                </div>
              )}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-border bg-muted/20 px-5 py-12 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
                <Icon name="TableProperties" className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-foreground">No indicators available</h3>
              <p className="mx-auto mt-2 max-w-lg text-sm text-muted-foreground">
                Add an RCC indicator to begin defining its sub-indicator groups and reporting rules.
              </p>
            </div>
          )}
        </article>
      </section>
    </div>
  );
}
